import React, { useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

function Dashboard() {
  useAuthGuard();

  const [rangeTo, setRangeTo] = useState('');
  const [downloads, setDownloads] = useState({ ios: 0, android: 0 });
  const [members, setMembers] = useState({ total: 0, churned: 0, active_1m: 0 });
  const [usage, setUsage] = useState({
    coupon: { count: 0 },
    ticket: { count: 0, pieces: { adult: 0, child: 0 } }
  });
  const [content, setContent] = useState({
    event: { published: { current: 0, not_published: 0 }, created_this_month: 0, updated_this_month: 0, deleted_this_month: 0 },
    spot: { published: { current: 0, not_published: 0 }, created_this_month: 0, updated_this_month: 0, deleted_this_month: 0 },
    coupon: { published: { current: 0, not_published: 0 }, created_this_month: 0, updated_this_month: 0, deleted_this_month: 0 }
  });
  const [sales, setSales] = useState({ ticket: { amount_month: 0 } });
  const fmtJPY = (n) => new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(n ?? 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fmt = (n) => new Intl.NumberFormat().format(n ?? 0);
  const fmtYmdJSTMinus1 = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const oneDayMs = 24 * 60 * 60 * 1000;
    const shifted = new Date(d.getTime() - oneDayMs); // range.to - 1 day
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      dateStyle: 'long', // → 2025年10月24日
    }).format(shifted);
  };
  // only change the effect; keep the rest of your component as-is
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('access_token') || '';
        const url = `${API_BASE_URL}/v1/summary`;
        console.debug('GET', url);
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setRangeTo(fmtYmdJSTMinus1(data?.range?.to));
        setDownloads({
          ios: data?.downloads?.ios ?? 0,
          android: data?.downloads?.android ?? 0,
        });
        setMembers({
          total: data?.members?.total ?? 0,
          churned: data?.members?.churned ?? 0,
          active_1m: data?.members?.active_1m ?? 0,
        });
        setUsage({
          coupon: { count: data?.usage?.coupon?.count ?? 0 },
          ticket: {
            count: data?.usage?.ticket?.count ?? 0,
            pieces: {
              adult: data?.usage?.ticket?.pieces?.adult ?? 0,
              child: data?.usage?.ticket?.pieces?.child ?? 0,
            },
            month_users: data?.usage?.ticket?.month_users ?? 0
          }
        });
        setContent({
          event: {
            published: {
              current: data?.content?.event?.published?.current ?? 0,
              not_published: data?.content?.event?.published?.not_published ?? 0
            },
            created_this_month: data?.content?.event?.created_this_month ?? 0,
            updated_this_month: data?.content?.event?.updated_this_month ?? 0,
            deleted_this_month: data?.content?.event?.deleted_this_month ?? 0
          },
          spot: {
            published: {
              current: data?.content?.spot?.published?.current ?? 0,
              not_published: data?.content?.spot?.published?.not_published ?? 0
            },
            created_this_month: data?.content?.spot?.created_this_month ?? 0,
            updated_this_month: data?.content?.spot?.updated_this_month ?? 0,
            deleted_this_month: data?.content?.spot?.deleted_this_month ?? 0
          },
          coupon: {
            published: {
              current: data?.content?.coupon?.published?.current ?? 0,
              not_published: data?.content?.coupon?.published?.not_published ?? 0
            },
            created_this_month: data?.content?.coupon?.created_this_month ?? 0,
            updated_this_month: data?.content?.coupon?.updated_this_month ?? 0,
            deleted_this_month: data?.content?.coupon?.deleted_this_month ?? 0
          }
        });
        setSales({
          ticket: {
            amount_month: data?.sales?.ticket?.amount_month ?? 0
          }
        });

      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  return (
    <Layout>

      <div className="pb-5">
        <div className="row g-4">
          <div className="col-12">
            <div className="col-12 row">
              <div className="col-6 mb-8">
                <h2 className="mb-2">ダッシュボード</h2>
                <h5 className="text-body-tertiary fw-semibold">
                  {loading ? 'Loading…' : (error ? `Error: ${error}` : '')}
                </h5>
              </div>
              <div className="col-6 mb-8 row">
                <p className="col-6 text-body-secondary fs-9 text-end">集計基準日時:</p>
                <h4 className="col-6 mb-0">{loading ? '—' : (rangeTo || '—')}</h4>
                {/* <button className="btn btn-link text-body px-0" type="button" >
                  <span className="fa-solid fa-file-export fs-9"></span>CSVダウンロード
                </button> */}
              </div>
            </div>
          </div>

          <div className="col-12 col-xxl-12">

            {/* 1段目: iOS, Android ダウンロード数 */}
            <div className="row align-items-center g-4 mb-4">
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <img
                    src="https://img.icons8.com/ios-filled/50/mac-os.png"
                    alt="iOS"
                    style={{ width: 46, height: 46 }}
                  />
                  <div className="ms-3">
                    <h4 className="mb-0">
                      {loading ? '—' : `${fmt(downloads.ios)} ダウンロード`}
                    </h4>
                    <p className="text-body-secondary fs-9 mb-0">iOS</p>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <img
                    src="https://img.icons8.com/ios-filled/50/android-os.png"
                    alt="Android"
                    style={{ width: 46, height: 46 }}
                  />
                  <div className="ms-3">
                    <h4 className="mb-0">
                      {loading ? '—' : `${fmt(downloads.android)} ダウンロード`}
                    </h4>
                    <p className="text-body-secondary fs-9 mb-0">Android</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 2段目: 会員数、退会者数、アクティブユーザー数 */}
            <div className="row align-items-center g-4 mb-4">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <img
                    src="https://img.icons8.com/ios-filled/50/user-group-man-man.png"
                    alt="会員数"
                    style={{ width: 46, height: 46 }}
                  />
                  <div className="ms-3">
                    <h4 className="mb-0">{loading ? '—' : fmt(members.total)} 名</h4>
                    <p className="text-body-secondary fs-9 mb-0">会員数</p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <img
                    src="https://img.icons8.com/ios-filled/50/user-male-circle.png"
                    alt="退会者数"
                    style={{ width: 46, height: 46 }}
                  />
                  <div className="ms-3">
                    <h4 className="mb-0">{loading ? '—' : fmt(members.churned)} 名</h4>
                    <p className="text-body-secondary fs-9 mb-0">退会者数</p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <img
                    src="https://img.icons8.com/ios-filled/50/activity-grid-2.png"
                    alt="アクティブユーザー"
                    style={{ width: 46, height: 46 }}
                  />
                  <div className="ms-3">
                    <h4 className="mb-0">{loading ? '—' : fmt(members.active_1m)} 人</h4>
                    <p className="text-body-secondary fs-9 mb-0">アクティブユーザー数</p>
                  </div>
                </div>
              </div>
            </div>

            {/* <hr className="bg-body-secondary mt-4" /> */}

            <div className="row flex-between-center mt-2 g-3">
              <div className="col-10">
                <div data-list='{"valueNames":["country","users","transactions","revenue","conv-rate"],"page":5}'>
                  <div className="mb-5 mt-7">
                    <h3>利用状況</h3>
                  </div>
                  <div className="table-responsive scrollbar">
                    <table className="table fs-10 mb-0">
                      <thead>
                        <tr>
                          <th className="border-top border-translucent ps-0 align-middle" style={{ width: '25%' }}></th>
                          <th className="border-top border-translucent text-end align-middle" style={{ width: '15%' }}>合計件数</th>
                          <th className="border-top border-translucent text-end align-middle" style={{ width: '15%' }}>合計枚数</th>
                          <th className="border-top border-translucent text-end align-middle" style={{ width: '15%' }}>大人枚数</th>
                          <th className="border-top border-translucent text-end pe-0 align-middle" style={{ width: '15%' }}>小人枚数</th>
                          <th className="border-top border-translucent text-end pe-0 align-middle" style={{ width: '15%' }}>今月の利用会員数</th>
                        </tr>
                      </thead>
                      <tbody className="list" id="table-regions-by-revenue">

                        <tr>
                          <td className="white-space-nowrap ps-0 country">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                <span className="nav-link-icon">
                                  <span data-feather="tag"></span>
                                </span>
                                <p className="mb-0 ps-3 text-primary fw-bold fs-9">クーポン</p>
                              </div>
                            </div>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmt(usage.coupon.count)}</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">-</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">-</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">-</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmt(usage.coupon.month_users)}</h5>
                          </td>
                        </tr>

                        <tr>
                          <td className="white-space-nowrap ps-0 country">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                <span className="nav-link-icon">
                                  <span data-feather="credit-card"></span>
                                </span>
                                <p className="mb-0 ps-3 text-primary fw-bold fs-9">乗車券</p>
                              </div>
                            </div>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmt(usage.ticket.count)}</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmt((usage.ticket.pieces.adult ?? 0) + (usage.ticket.pieces.child ?? 0))}</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmt(usage.ticket.pieces.adult)}</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmt(usage.ticket.pieces.child)}</h5>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmt(usage.ticket.month_users)}</h5>
                          </td>
                        </tr>

                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="row flex-between-center mt-2 g-3">
              <div className="col-10">
                <div data-list='{"valueNames":["country","users","transactions","revenue","conv-rate"],"page":5}'>
                  <div className="mb-5 mt-7">
                    <h3>コンテンツ状況</h3>
                  </div>
                  <div className="table-responsive scrollbar">
                    <table className="table fs-10 mb-0">
                      <thead>
                        <tr>
                          <th className="border-top border-translucent ps-0 align-middle" style={{ width: '25%' }}></th>
                          <th className="border-top border-translucent text-end align-middle" style={{ width: '15%' }}>公開中</th>
                          <th className="border-top border-translucent text-end align-middle" style={{ width: '15%' }}>非公開</th>
                          <th className="border-top border-translucent text-end align-middle" style={{ width: '15%' }}>今月登録</th>
                          <th className="border-top border-translucent text-end pe-0 align-middle" style={{ width: '15%' }}>今月更新</th>
                          <th className="border-top border-translucent text-end pe-0 align-middle" style={{ width: '15%' }}>今月削除</th>
                        </tr>
                      </thead>
                      <tbody className="list" id="table-regions-by-revenue">

                        <tr>
                          <td className="white-space-nowrap ps-0 country">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                <span className="nav-link-icon">
                                  <span data-feather="calendar"></span>
                                </span>
                                <p className="mb-0 ps-3 text-primary fw-bold fs-9">キャンペーン</p>
                              </div>
                            </div>
                          </td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.event.published.current)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.event.published.not_published)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.event.created_this_month)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.event.updated_this_month)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.event.deleted_this_month)}</h5></td>
                        </tr>
                        <tr>
                          <td className="white-space-nowrap ps-0 country">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                <span className="nav-link-icon">
                                  <span data-feather="map-pin"></span>
                                </span>
                                <p className="mb-0 ps-3 text-primary fw-bold fs-9">スポット</p>
                              </div>
                            </div>
                          </td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.spot.published.current)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.spot.published.not_published)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.spot.created_this_month)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.spot.updated_this_month)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.spot.deleted_this_month)}</h5></td>
                        </tr>
                        <tr>
                          <td className="white-space-nowrap ps-0 country">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                <span className="nav-link-icon">
                                  <span data-feather="tag"></span>
                                </span>
                                <p className="mb-0 ps-3 text-primary fw-bold fs-9">クーポン</p>
                              </div>
                            </div>
                          </td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.coupon.published.current)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.coupon.published.not_published)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.coupon.created_this_month)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.coupon.updated_this_month)}</h5></td>
                          <td className="align-middle text-end"><h5 className="mb-0">{loading ? '—' : fmt(content.coupon.deleted_this_month)}</h5></td>
                        </tr>

                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="row flex-between-center mt-2 g-3">
              <div className="col-10">
                <div data-list='{"valueNames":["country","users","transactions","revenue","conv-rate"],"page":5}'>
                  <div className="mb-5 mt-7">
                    <h3>販売実績</h3>
                  </div>
                  <div className="table-responsive scrollbar">
                    <table className="table fs-10 mb-0">
                      <thead>
                        <tr>
                          <th className="border-top border-translucent ps-0 align-middle" style={{ width: '25%' }}></th>
                          <th className="border-top border-translucent text-end pe-0 align-middle" style={{ width: '15%' }}>今月合計金額</th>
                        </tr>
                      </thead>
                      <tbody className="list" id="table-regions-by-revenue">
                        <tr>
                          <td className="white-space-nowrap ps-0 country">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center">
                                <span className="nav-link-icon">
                                  <span data-feather="tag"></span>
                                </span>
                                <p className="mb-0 ps-3 text-primary fw-bold fs-9">乗車券</p>
                              </div>
                            </div>
                          </td>
                          <td className="align-middle text-end">
                            <h5 className="mb-0">{loading ? '—' : fmtJPY(sales.ticket.amount_month)}</h5>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

    </Layout>
  );
}

export default Dashboard;
